<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import SavedViewAppearancePicker from '@/components/savedViews/SavedViewAppearancePicker.vue';
import { useDeleteSavedView, useUpdateSavedView } from '@/composables/api/useSavedViewsApi';
import { useMountedWhileOpen } from '@/composables/useMountedWhileOpen';
import { useSavedViewRowActions, type SavedViewRowDialog } from '@/layouts/frame/savedViewRowActions';
import type { SavedViewRecord } from '@/utils/savedViews';
import { usePersistentTableStore } from '@/stores/persistentTableStore';

/**
 * The Rename, "Icon and colour" and Delete dialogs for the sidebar's saved
 * views. The sidebar holds one set of them. Each row's "…" menu says which view
 * to act on, through useSavedViewRowActions.
 *
 * Each dialog is in the page only while it is open or fading out, so a closed
 * one leaves no Teleport behind (see useMountedWhileOpen).
 */
const props = defineProps<{ views: SavedViewRecord[] }>();

const { targetView, openDialogName, closeDialog } = useSavedViewRowActions();

const updateMutation = useUpdateSavedView();
const deleteMutation = useDeleteSavedView();
const tableStore = usePersistentTableStore();

/**
 * The view as the sidebar lists it now, so the icon picker shows a pick as soon
 * as the mutation patches the list. It falls back to the view the menu passed
 * once the list no longer holds it, for example while the Delete dialog fades out.
 */
const view = computed(() => {
    const passedView = targetView.value;
    if (passedView === null) {
        return null;
    }
    return props.views.find((candidate) => candidate.id === passedView.id && candidate.entity === passedView.entity) ?? passedView;
});

function dialogOpenModel(dialog: SavedViewRowDialog) {
    return computed({
        get: () => openDialogName.value === dialog,
        set: (isOpen: boolean) => {
            if (!isOpen && openDialogName.value === dialog) {
                closeDialog();
            }
        },
    });
}

const renameOpen = dialogOpenModel('rename');
const appearanceOpen = dialogOpenModel('appearance');
const deleteOpen = dialogOpenModel('delete');

const { mounted: renameDialogMounted, onCloseFinished: onRenameDialogClosed } = useMountedWhileOpen(renameOpen);
const { mounted: deleteDialogMounted, onCloseFinished: onDeleteDialogClosed } = useMountedWhileOpen(deleteOpen);

const renameValue = ref('');
watch(renameOpen, (isOpen) => {
    if (isOpen) {
        renameValue.value = view.value?.name ?? '';
    }
});

function rename() {
    const renamedView = view.value;
    const name = renameValue.value.trim();
    if (renamedView === null || name === '' || name === renamedView.name) {
        renameOpen.value = false;
        return;
    }
    updateMutation.mutate({
        entity: renamedView.entity,
        search: '',
        savedViewId: renamedView.id,
        name,
        definition: renamedView.definition,
    });
    renameOpen.value = false;
}

function confirmDelete() {
    const deletedView = view.value;
    if (deletedView === null) {
        return;
    }
    // Deleting the view the list is in leaves the view, so the Save button and
    // the address stop naming a view that is gone.
    const wasOpen = tableStore.storeKey === deletedView.entity && tableStore.activeSavedViewId === deletedView.id;
    if (wasOpen) {
        tableStore.setActiveSavedViewIdForScope(deletedView.entity, null);
    }
    deleteMutation.mutate({ entity: deletedView.entity, search: '', savedViewId: deletedView.id });
    deleteOpen.value = false;
}
</script>

<template>
  <Dialog v-if="renameDialogMounted" v-model:open="renameOpen">
    <DialogContent class="sm:max-w-md" @close-auto-focus="onRenameDialogClosed">
      <DialogHeader>
        <DialogTitle>Rename view</DialogTitle>
        <DialogDescription class="sr-only">Type a new name for this saved view.</DialogDescription>
      </DialogHeader>
      <form @submit.prevent="rename">
        <label for="rename-saved-view" class="sr-only">Name</label>
        <Input id="rename-saved-view" v-model="renameValue" data-testid="saved-view-rename-input" autofocus />
      </form>
      <DialogFooter>
        <Button variant="outline" @click="renameOpen = false">Cancel</Button>
        <Button data-testid="saved-view-rename-save" @click="rename">Rename</Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>

  <SavedViewAppearancePicker v-if="view" v-model:open="appearanceOpen" :view="view" />

  <Dialog v-if="deleteDialogMounted" v-model:open="deleteOpen">
    <DialogContent class="sm:max-w-md" @close-auto-focus="onDeleteDialogClosed">
      <DialogHeader><DialogTitle>Delete "{{ view?.name }}"?</DialogTitle></DialogHeader>
      <DialogDescription>This deletes your saved view. Copies other people imported are not affected.</DialogDescription>
      <DialogFooter>
        <Button variant="outline" @click="deleteOpen = false">Cancel</Button>
        <Button variant="destructive" data-testid="saved-view-delete-confirm" @click="confirmDelete">Delete</Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
</template>
